/**
 * Main App Component with Routing
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CurrencyProvider } from './hooks/useCurrency';
import { VMContextProvider } from './context/VMContext';
import { useClarityTracking } from './hooks/useClarityTracking';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseLabs from './pages/CourseLabs';
import Labs from './pages/Labs';
import LabTerminal from './pages/LabTerminal';
import Progress from './pages/Progress';
import MockTests from './pages/MockTests';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import Pricing from './pages/Pricing';
import ExamRHCSA from './pages/ExamRHCSA';
import ExamDO188 from './pages/ExamDO188';
import ExamEX294 from './pages/ExamEX294';
import Home from './pages/Home';
import Navbar from './components/Navbar';

// Admin Pages (Separate from user auth)
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Support / Help Center Pages
import SupportPage from './pages/SupportPage';
import SupportLogin from './pages/SupportLogin';
import SupportDashboard from './pages/SupportDashboard';

// Projects
import Projects from './pages/Projects';

// Landing Pages
import DevOpsLanding from './pages/DevOpsLanding';
import RHCSALanding from './pages/RHCSALanding';

// Exam Discount Eligibility (public)
import ExamBooking from './pages/ExamBooking';

// Leaderboard
import LeaderboardPage from './pages/LeaderboardPage';

// Notifications
import NotificationsPage from './pages/Notifications';
import NotificationBell from './components/NotificationBell';

// Email tracking
import Unsubscribed from './pages/Unsubscribed';

// Profile
import ProfilePage from './pages/ProfilePage';
import JobsPage from './pages/JobsPage';

// Components
import AnnouncementBanner from './components/AnnouncementBanner';
import VMStatusBar from './components/VMStatusBar';

// --- Skip React Router for ttyd lab URLs ---
if (window.location.pathname.startsWith('/lab/')) {
  // Let Nginx/ttyd handle this URL directly
  window.location.replace(window.location.href);
}




// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Main App Layout Component
const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  const isSupportDashRoute = window.location.pathname.startsWith('/support/dashboard') || window.location.pathname === '/support';
  const isHomePage = window.location.pathname === '/' || window.location.pathname === '/home';
  const isLandingPage = window.location.pathname === '/devops' || window.location.pathname === '/rhcsa';
  
  return (
    <VMContextProvider>
    <div className="min-h-screen bg-slate-900">
      {/* Show announcement on all pages except admin, support dashboard, home, and landing pages */}
      {!isAdminRoute && !isSupportDashRoute && !isHomePage && !isLandingPage && <AnnouncementBanner />}
      {isAuthenticated && !isAdminRoute && !isSupportDashRoute && !isLandingPage && <Navbar />}
      <main className={isAuthenticated && !isAdminRoute && !isSupportDashRoute && !isLandingPage ? 'pt-16' : ''}>
        {children}
      </main>
      {/* Floating Notification Bell — shown for authenticated students (not admin/support/landing) */}
      {isAuthenticated && !isAdminRoute && !isSupportDashRoute && !isLandingPage && <NotificationBell />}
      {isAuthenticated && !isAdminRoute && !isSupportDashRoute && !isLandingPage && <VMStatusBar />}
    </div>
    </VMContextProvider>
  );
};

// Home or Dashboard based on auth status
const HomeOrDashboard = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Home />;
};

// App Routes Component
const AppRoutes = () => {
  // Microsoft Clarity SPA route-change tracker — fires on every navigation
  // so each path shows up as a distinct page view in the Clarity dashboard.
  useClarityTracking();
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect EXAM_RHCSA course page to exam page */}
      <Route
        path="/courses/EXAM_RHCSA"
        element={<Navigate to="/exam/rhcsa" replace />}
      />
      
      {/* Redirect EXAM_DO188 course page to exam page */}
      <Route
        path="/courses/EXAM_DO188"
        element={<Navigate to="/exam/do188" replace />}
      />
      
      {/* Redirect EXAM_EX294 course page to exam page */}
      <Route
        path="/courses/EXAM_EX294"
        element={<Navigate to="/exam/ex294" replace />}
      />
      
      <Route
        path="/courses/:course"
        element={
          <ProtectedRoute>
            <CourseLabs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/labs/:course/:labId"
        element={
          <ProtectedRoute>
            <Labs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab-terminal/:course/:labId"
        element={
          <ProtectedRoute>
            <LabTerminal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mock-tests"
        element={
          <ProtectedRoute>
            <MockTests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      
      {/* Payment Routes */}
      <Route
        path="/payment/:courseId"
        element={
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      
      {/* Google Ads Landing Page - Redirects to Login */}
      <Route path="/devops" element={<DevOpsLanding />} />
      <Route path="/rhcsa" element={<RHCSALanding />} />

      {/* Exam Discount Eligibility — public booking form */}
      <Route path="/exam-booking" element={<ExamBooking />} />
      
      {/* RHCSA Exam Practice */}
      <Route
        path="/exam/rhcsa"
        element={
          <ProtectedRoute>
            <ExamRHCSA />
          </ProtectedRoute>
        }
      />
      
      {/* DO188 Container Exam Practice */}
      <Route
        path="/exam/do188"
        element={
          <ProtectedRoute>
            <ExamDO188 />
          </ProtectedRoute>
        }
      />
      
      {/* EX294 Ansible Exam Practice */}
      <Route
        path="/exam/ex294"
        element={
          <ProtectedRoute>
            <ExamEX294 />
          </ProtectedRoute>
        }
      />
      
      {/* Public Home Page for unauthenticated users */}
      <Route path="/home" element={<Home />} />
      
      {/* Admin Routes (Separate auth system) */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      
      {/* Support / Help Center Routes */}
      <Route path="/support" element={<SupportLogin />} />
      <Route path="/support/dashboard" element={<SupportDashboard />} />
      <Route path="/help-center" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />

      {/* Projects */}
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />

      {/* Progress - visual learning progress */}
      <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />

      {/* Leaderboard - peer rankings + badges */}
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />

      {/* Notifications inbox */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* User Profile */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* Jobs Board */}
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />

      {/* Email unsubscribe confirmation — public, no auth required */}
      <Route path="/unsubscribed" element={<Unsubscribed />} />

      {/* Root - Show Home for unauthenticated, Dashboard for authenticated */}
      <Route path="/" element={<HomeOrDashboard />} />
      
      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <HelmetProvider>
      <CurrencyProvider>
        <AuthProvider>
          <Router>
            <AppLayout>
              <AppRoutes />
            </AppLayout>
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'rgb(30 41 59)',
                  color: 'rgb(248 250 252)',
                  border: '1px solid rgb(71 85 105)',
                },
              }}
            />
          </Router>
        </AuthProvider>
      </CurrencyProvider>
    </HelmetProvider>
  );
};

export default App;
